import { AlertDialog, ConfirmationDialog } from '@paintscout/ui'
import { DialogStackContext, useClientOptions, useUser } from '@paintscout/ui-business'
import * as Sentry from '@sentry/react'
import {
  getPayments,
  getQuoteOptions,
  hasIntegrationInterface,
  isValidPaymentRequest,
  orderPaymentsWithChildren,
  requestHasProcessingPayment,
  setPayments,
  uuid
} from '@paintscout/util/builder'
import { isEmailValid } from '@paintscout/util/util'
import type { Payment, PayrixPaymentType, QuoteDocument } from 'paintscout'
import React, { useContext } from 'react'
import { useSnackbar } from 'notistack'
import { QuoteContext } from '../../../../context/QuoteContext'
import type { ConfiguredPaymentRequest } from '../../../../dialogs'
import {
  CollectPayrixPaymentDialog,
  ConfigurePaymentDialog,
  QuotePdfDialog,
  RefundPaymentDialog,
  SendPaymentRequestDialog,
  TileListDialog,
  useOpenPaymentDialog
} from '../../../../dialogs'
import { useCheckForNewPayments } from '../../../../util/useCheckForNewPayments'

const CollectStripePaymentDialog = React.lazy(() => import('../../../../dialogs/CollectStripePaymentDialog'))

export const RECORD_PAYMENT_KEY = 'record-payment'

export interface UsePaymentActionsArgs {
  /** True when rendering inside a customer-facing presentation (hides contractor-only controls). */
  inPresentation?: boolean
  /** Called with the updated quote when a payment is removed (PaymentsTable persists it). */
  onChange?: (quote: QuoteDocument) => void
}

/**
 * The estimate/invoice payment actions — the Add/Request-Payment flow (Record / Request / Take CC /
 * Take ACH) and the shared edit / configure / collect / delete handlers — extracted from
 * `PaymentsTable` so both the table and the estimateNext header's "+ Add → Payment" open the exact
 * same dialogs. Sources the quote, dialogs, options, user, and flags from context/hooks internally;
 * eligibility (`canCollectCCPayment`, `isPaymentEligible`, …) is returned for callers to gate UI.
 */
export function usePaymentActions({ inPresentation, onChange }: UsePaymentActionsArgs = {}) {
  const { quote, save: saveQuote, updateQuote, isDirty } = useContext(QuoteContext)
  const { openDialog, dismissDialog, dismissAllDialogs } = useContext(DialogStackContext)
  const openPaymentDialog = useOpenPaymentDialog()
  const { options, clientId } = useClientOptions()
  const { user, preferences, isPainter } = useUser()
  const { enqueueSnackbar } = useSnackbar()
  // Refetch before recording a payment so we don't stack one on top of a payment taken elsewhere
  // (customer view, another estimator) since this page loaded — #21913.
  const { checking: checkingForNewPayments, check: checkForNewPayments } = useCheckForNewPayments()

  const { allowPaymentsOnInvoices, allowACHPaymentsOnInvoices } = getQuoteOptions({ quote, options })
  const hasStripe = hasIntegrationInterface({ preferences, options, integration: 'stripe' })
  const hasStripeW1d = hasIntegrationInterface({ preferences, options, integration: 'stripeW1d' })
  const hasPayrix = hasIntegrationInterface({ preferences, options, integration: 'payrix' })
  const isCanadianPayrix = options.options.integrations?.payrix?.config?.country === 'Canada' && hasPayrix
  const achLabel = isCanadianPayrix ? 'EFT' : 'ACH'

  const hasPaymentProcessing = hasStripe || hasStripeW1d || hasPayrix
  const isPaymentEligible = quote.totals.after_tax > 0 && quote.totals.balance_due > 0

  const canCollectCCPayment = hasPaymentProcessing && allowPaymentsOnInvoices && isPaymentEligible
  const canCollectACHPayment = hasPayrix && allowACHPaymentsOnInvoices && isPaymentEligible

  const payments = getPayments({ quote })
    .filter((payment) => (inPresentation ? payment.status !== 'pending' : true))
    .map((payment) => {
      if (payment.provider === 'payrix' && payment.providerDetails?.payrix?.isCanadian) {
        return {
          ...payment,
          type: payment.type?.toLowerCase() === 'ach' ? 'EFT' : payment.type
        }
      } else {
        return payment
      }
    })

  // Payments.findIndex()-based row actions (PaymentsTable/PaymentsTableNext) re-index into a
  // freshly-fetched, unfiltered getPayments({ quote }) array, so `payments` above must keep its
  // full length/order outside presentation. Stale requests are hidden from what's rendered via a
  // separate, shorter list instead of shrinking `payments` itself — a pending request whose amount
  // now exceeds the balance due (e.g. a separate payment already covered the invoice) is no longer
  // worth showing as actionable in the editor, matching how presentation never shows pending rows.
  // A request with an in-flight processing child stays visible so that child row isn't orphaned.
  const visiblePayments = inPresentation
    ? payments
    : payments.filter(
        (payment) =>
          payment.status !== 'pending' ||
          isValidPaymentRequest({ quote, payment }) ||
          requestHasProcessingPayment({ quote, payment })
      )

  const displayPayments = orderPaymentsWithChildren(visiblePayments)

  function deletePayment(index: number) {
    const freshPayments = getPayments({ quote })
    const payment = freshPayments[index]
    openDialog(ConfirmationDialog, {
      message: `Are you sure you want to remove this payment${payment?.status === 'pending' ? ' request' : ''}?`,
      closeButton: true,
      noLabel: 'Back',
      onNoLabelClick: dismissDialog,
      yesLabel: "Yes, I'm Sure",
      onConfirm: async () => {
        freshPayments.splice(index, 1)

        const updatedQuote = setPayments({ quote, payments: freshPayments, options })

        onChange?.(updatedQuote)
        await saveQuote(updatedQuote)
        dismissAllDialogs()
      },
      onCancel: dismissAllDialogs
    })
  }

  function handleEditPayment({
    paymentIndex,
    backOnCancel,
    markingPaid,
    // The doc the dialog should write back to — the freshly-fetched one when the staleness check
    // was able to adopt it, otherwise the local (possibly dirty) quote.
    quote: quoteToEdit = quote
  }: {
    paymentIndex?: number
    backOnCancel?: boolean
    markingPaid?: boolean
    quote?: QuoteDocument
  } = {}) {
    const payment = payments[paymentIndex]
    const isProcessedPayment = !!payment?.activityId && !payment?.manual
    openPaymentDialog({
      payment,
      quote: quoteToEdit,
      paymentIndex,
      backOnCancel,
      markingPaid,
      inPresentation,
      onDelete: !isProcessedPayment && paymentIndex !== undefined ? () => deletePayment(paymentIndex) : undefined,
      onConfirm: dismissAllDialogs,
      onCancel: dismissDialog,
      // Painters can't reliably download PDF receipts (mobile) and don't need to — customers
      // are emailed a receipt on payment. Hide the receipt action for painter accounts (ENG-846).
      onReceipt: isPainter
        ? undefined
        : async (payment) => {
            if (isDirty) {
              await saveQuote()
            }
            openDialog(QuotePdfDialog, {
              quote,
              companyId: clientId,
              options,
              silent: true,
              view: 'payment-receipt',
              paymentId: payment.id,
              onConfirm: dismissDialog
            })
          },
      // Painters can't refund (ENG-870) — withholding onRefund hides the inline Refund
      // button in ViewPaymentDialog (it only renders when onRefund is passed). Mirrors
      // the action-menu refund gate in the tables.
      onRefund:
        inPresentation || isPainter
          ? undefined
          : (payment) => {
              openDialog(RefundPaymentDialog, {
                payment,
                allPayments: getPayments({ quote }),
                onClose: dismissDialog
              })
            },
      onSaveNote: inPresentation
        ? undefined
        : (updatedQuote) => {
            updateQuote({ quote: updatedQuote })
          }
    })
  }

  function handleRequestPayment(quoteToRequestOn: QuoteDocument = quote) {
    if (!quoteToRequestOn?.contact) {
      openDialog(AlertDialog, {
        title: 'Contact Required',
        message: 'You must add a contact before you can request a payment',
        onConfirm: dismissAllDialogs
      })
      return
    } else if (!isEmailValid(quoteToRequestOn.contact.email)) {
      openDialog(AlertDialog, {
        title: 'Invalid Email',
        message: 'You must add a valid email address to your contact before you can request a payment',
        onConfirm: dismissAllDialogs
      })
      return
    }
    openDialog(ConfigurePaymentDialog, {
      quote: quoteToRequestOn,
      preferences,
      isRequest: true,
      buttonLabel: 'Next',
      backOnCancel: true,
      onConfirm: (args: ConfiguredPaymentRequest) => {
        const payment: Payment = {
          ...args,
          id: uuid(),
          type: null,
          date: Date.now(),
          status: 'pending',
          isRequest: true
        }
        openDialog(SendPaymentRequestDialog, {
          payment,
          user,
          preferences
        })
      },
      onCancel: dismissDialog
    })
  }

  function handleConfigurePayment(paymentType: PayrixPaymentType = 'credit', quoteToCollectOn: QuoteDocument = quote) {
    openDialog(ConfigurePaymentDialog, {
      quote: quoteToCollectOn,
      preferences,
      backOnCancel: true,
      hideSurcharge: paymentType === 'ach',
      onCancel: dismissDialog,
      onConfirm: (paymentArgs) => handleCollectPayment({ ...paymentArgs, paymentType, quote: quoteToCollectOn })
    })
  }

  function handleCollectPayment({
    amount,
    note,
    deposit,
    paymentRequestId,
    paymentType,
    quote: quoteToCollectOn = quote
  }: {
    amount: number
    note: string
    deposit: boolean
    paymentRequestId?: string
    paymentType?: PayrixPaymentType
    quote?: QuoteDocument
  }) {
    if (!hasPaymentProcessing) {
      Sentry.captureException(new Error('CollectPaymentDialog tried to open without a valid payment provider'))
      enqueueSnackbar(
        'Something is wrong with your payment integration. Please contact our support team for assistance.',
        { variant: 'error' }
      )
      dismissAllDialogs()
      return
    }
    const PaymentDialog = hasPayrix ? CollectPayrixPaymentDialog : CollectStripePaymentDialog
    openDialog(PaymentDialog, {
      quote: quoteToCollectOn,
      amount,
      note,
      options,
      companyId: clientId,
      isDeposit: deposit,
      source: 'collect-payment',
      paymentRequestId,
      paymentType,
      backOnCancel: true,
      onCancel: dismissDialog,
      onConfirm: async (updatedQuote: QuoteDocument) => {
        dismissAllDialogs()
        await updateQuote({ quote: updatedQuote, autosave: false, blockDirty: true })
      }
    })
  }

  async function handleAddRequestPayment() {
    // Refuse to open the payment flow on a stale copy — if a payment landed on the server since this
    // page loaded, the user gets a warning + a refreshed table and has to click again (#21913).
    const { blocked, quote: freshQuote } = await checkForNewPayments()
    if (blocked) {
      return
    }

    if (!isPaymentEligible && !canCollectCCPayment && !canCollectACHPayment) {
      handleEditPayment({ quote: freshQuote })
      return
    }
    openDialog(TileListDialog, {
      title: 'Add/Request Payment',
      showTopClose: true,
      items: [
        {
          key: RECORD_PAYMENT_KEY,
          label: 'Record Payment'
        },
        isPaymentEligible && {
          key: 'request-payment',
          label: 'Request Payment'
        },
        canCollectCCPayment && {
          key: 'credit',
          label: 'Take Credit Card Payment'
        },
        canCollectACHPayment && {
          key: 'ach',
          label: `Take ${achLabel} Payment`
        }
      ].filter(Boolean),
      onItemSelect: (key: string) => {
        if (key === RECORD_PAYMENT_KEY) {
          handleEditPayment({ backOnCancel: true, quote: freshQuote })
        } else if (key === 'credit' || key === 'ach') {
          handleConfigurePayment(key, freshQuote)
        } else if (key === 'request-payment') {
          handleRequestPayment(freshQuote)
        }
      }
    })
  }

  return {
    payments,
    displayPayments,
    achLabel,
    hasPaymentProcessing,
    isPaymentEligible,
    canCollectCCPayment,
    canCollectACHPayment,
    deletePayment,
    handleEditPayment,
    handleRequestPayment,
    handleConfigurePayment,
    handleCollectPayment,
    handleAddRequestPayment,
    /** True while the #21913 staleness refetch is in flight — for the Add Payment button's spinner. */
    checkingForNewPayments
  }
}
